import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, CircleDot, CornerDownLeft, Workflow as WorkflowIcon, X } from "lucide-react";
import type { FormButtonElement, WorkflowGraph, WorkflowGraphStage, WorkflowGraphTransition } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useLocale } from "../react/providers/LocaleProvider";
import { fmtDateTime } from "../widgets/builtins";
import { HeaderButton } from "./FormHeader";

// Workflow drawer (communication Enh 03 Slice 7, frozen variant: vertical
// metro rail + "From here, you can…" footer) — the record strip's ⑆ chip
// surface for workflow-driven records (any model with a workflow=True field).
//
// Read-only, four states per the frozen legend:
//   ✓ visited (solid, with who/when) · ● current (pulsing) ·
//   dashed = available next FOR THE CALLING USER (guards evaluated
//   server-side) · dimmed = not reachable yet.
// Backward transitions (return/reject) render as side spurs at the stage
// they leave from. Data comes from ONE round trip: GET /api/workflow/graph.

export interface RailStop {
    stage: WorkflowGraphStage;
    /** Forward state for the rail node. */
    state: "visited" | "current" | "next" | "other";
    /** Backward/sideways transitions leaving THIS stage (only on current). */
    spurs: Array<{ transition: WorkflowGraphTransition; toLabel: string }>;
}

/** Project the wire graph onto the metro rail's per-stop view model. */
export function buildRail(graph: WorkflowGraph): RailStop[] {
    const byId = new Map(graph.stages.map((s) => [s.id, s]));
    const currentSeq = graph.stages.find((s) => s.is_current)?.sequence ?? null;
    const nextStageIds = new Set(
        graph.transitions
            .filter((t) => t.from_current && t.available)
            .map((t) => t.to_stage_id),
    );
    return graph.stages.map((stage) => {
        let state: RailStop["state"] = "other";
        if (stage.is_current) state = "current";
        else if (stage.is_visited) state = "visited";
        else if (nextStageIds.has(stage.id)) state = "next";
        const spurs = stage.is_current
            ? graph.transitions
                  .filter((t) => {
                      if (!t.from_current) return false;
                      const to = byId.get(t.to_stage_id);
                      // A spur is any transition that does NOT move strictly
                      // forward in sequence (returns/rejects), plus forward
                      // ones the rail already shows as "next" are skipped.
                      return to ? (currentSeq === null || to.sequence <= currentSeq) : false;
                  })
                  .map((t) => ({ transition: t, toLabel: byId.get(t.to_stage_id)?.label ?? t.to_stage_id }))
            : [];
        return { stage, state, spurs };
    });
}

/** Transitions leaving the current stage — the "From here, you can…" list. */
export function fromCurrent(graph: WorkflowGraph): Array<{ transition: WorkflowGraphTransition; toLabel: string }> {
    const byId = new Map(graph.stages.map((s) => [s.id, s]));
    return graph.transitions
        .filter((t) => t.from_current)
        .map((t) => ({ transition: t, toLabel: byId.get(t.to_stage_id)?.label ?? t.to_stage_id }));
}

function fmtWhen(iso: string | null, tz: string): string {
    if (!iso) return "";
    return fmtDateTime(iso, tz);
}

interface WorkflowDrawerViewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    graph: WorkflowGraph | null;
    loading: boolean;
    timezone?: string;
    /** Record action verbs (the form <header> buttons) — surfaced as footer
        buttons alongside the available transitions (Enh 14 Stage 4). */
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
    actionSlug?: string;
    recordRef?: string | null;
}

export function WorkflowDrawerView({
    open,
    onOpenChange,
    title,
    graph,
    loading,
    timezone = "UTC",
    headerButtons,
    onRunButton,
    onRunTransition,
    actionSlug,
    recordRef,
}: WorkflowDrawerViewProps): ReactNode {
    const rail = graph ? buildRail(graph) : [];
    const nextActions = graph ? fromCurrent(graph) : [];
    // Footer = the user-runnable next steps: available transitions (guards
    // pass) + the record's action verbs. Mirrors the mockup's `.rc-drawer__foot`.
    const availableNext = nextActions.filter((a) => a.transition.available);
    const footerVerbs = headerButtons ?? [];
    const showFoot = Boolean(graph && (availableNext.length > 0 || footerVerbs.length > 0));
    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" />
                <Dialog.Content className="wf-drawer" aria-describedby={undefined}>
                    <header className="wf-drawer__head">
                        <WorkflowIcon size={15} aria-hidden="true" />
                        <Dialog.Title className="wf-drawer__title">
                            Workflow{graph?.definition ? ` — ${graph.definition.name}` : ""}
                        </Dialog.Title>
                        <span className="wf-drawer__sub">· {title}</span>
                        <Dialog.Close className="wf-drawer__close" aria-label="Close">
                            <X size={15} />
                        </Dialog.Close>
                    </header>

                    <div className="wf-drawer__body">
                        {loading ? (
                            <p className="wf-drawer__empty">Loading…</p>
                        ) : !graph || !graph.definition ? (
                            <p className="wf-drawer__empty">No workflow is configured for this record.</p>
                        ) : (
                            <>
                                <div className="wf-drawer__legend">
                                    <span><i className="wf-dot wf-dot--visited" /> Visited</span>
                                    <span><i className="wf-dot wf-dot--current" /> Current</span>
                                    <span><i className="wf-dot wf-dot--next" /> Available next (for you)</span>
                                    <span><i className="wf-dot wf-dot--other" /> Later</span>
                                </div>

                                {graph.pending_transition_code ? (
                                    <p className="wf-drawer__pending">
                                        ⏳ Pending transition: <b>{graph.pending_transition_code}</b> — awaiting approval
                                    </p>
                                ) : null}

                                <div className="wf-rail">
                                    {rail.map((stop) => (
                                        <div className={`wf-stop wf-stop--${stop.state}`} key={stop.stage.id} data-state={stop.state}>
                                            <div className="wf-stop__title">
                                                {stop.stage.label}
                                                {stop.state === "visited" ? (
                                                    <span className="wf-tag wf-tag--visited"><Check size={10} /> visited</span>
                                                ) : null}
                                                {stop.state === "current" ? (
                                                    <span className="wf-tag wf-tag--current"><CircleDot size={10} /> you are here</span>
                                                ) : null}
                                                {stop.state === "next" ? (
                                                    <span className="wf-tag wf-tag--next">possible next</span>
                                                ) : null}
                                            </div>
                                            {stop.stage.is_visited && stop.stage.visited_at ? (
                                                <div className="wf-stop__sub">
                                                    Entered {fmtWhen(stop.stage.visited_at, timezone)}
                                                    {stop.stage.visited_by ? ` · by ${stop.stage.visited_by}` : ""}
                                                </div>
                                            ) : null}
                                            {stop.spurs.map(({ transition, toLabel }) => (
                                                <div
                                                    className={`wf-spur${transition.available ? "" : " wf-spur--blocked"}`}
                                                    key={transition.id}
                                                >
                                                    <CornerDownLeft size={11} aria-hidden="true" />
                                                    {transition.name} → {toLabel}
                                                    {transition.available ? " — available to you" : " — not permitted"}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>

                                <div className="wf-drawer__next">
                                    <h5>From here, you can…</h5>
                                    {nextActions.length === 0 ? (
                                        <p className="wf-drawer__empty">No outgoing transitions from the current stage.</p>
                                    ) : (
                                        nextActions.map(({ transition, toLabel }) => (
                                            <div
                                                className={`wf-next-row${transition.available ? " is-available" : ""}`}
                                                key={transition.id}
                                            >
                                                <b>{transition.name}</b> → {toLabel}
                                                {transition.requires_approval ? (
                                                    <span className="wf-tag wf-tag--next">needs approval</span>
                                                ) : null}
                                                <span className="wf-next-row__verdict">
                                                    {transition.available ? "you qualify" : "not permitted"}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {showFoot ? (
                        <footer className="wf-drawer__foot">
                            <div className="wf-drawer__foot-cap">From here, you can…</div>
                            <div className="wf-drawer__foot-row">
                                {footerVerbs.map((el) => (
                                    <HeaderButton
                                        key={`v-${el.name}`}
                                        el={el}
                                        record={null}
                                        actionSlug={actionSlug ?? ""}
                                        recordRef={recordRef ?? null}
                                        onClick={onRunButton}
                                        className="wf-foot-btn"
                                    />
                                ))}
                                {availableNext.map(({ transition }, i) => (
                                    <button
                                        key={transition.id}
                                        type="button"
                                        className={`wf-foot-btn${i === 0 && footerVerbs.length === 0 ? " wf-foot-btn--primary" : ""}`}
                                        onClick={() => {
                                            void onRunTransition?.(transition.code, transition.name);
                                            onOpenChange(false);
                                        }}
                                    >
                                        {transition.name}
                                        {transition.requires_approval ? " →" : ""}
                                    </button>
                                ))}
                            </div>
                        </footer>
                    ) : null}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

interface WorkflowDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    modelKey: string;
    recordUuid: string;
    title: string;
    /** Footer next-step buttons (Enh 14 Stage 4). */
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
    actionSlug?: string;
}

export function WorkflowDrawer({
    open,
    onOpenChange,
    modelKey,
    recordUuid,
    title,
    headerButtons,
    onRunButton,
    onRunTransition,
    actionSlug,
}: WorkflowDrawerProps): ReactNode {
    const runtime = useRuntime();
    const locale = useLocale();
    const [graph, setGraph] = useState<WorkflowGraph | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        runtime.services.workflow
            .graph(modelKey, recordUuid)
            .then((g) => {
                if (active) setGraph(g);
            })
            .catch(() => {
                if (active) setGraph(null);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, modelKey, recordUuid]);

    return (
        <WorkflowDrawerView
            open={open}
            onOpenChange={onOpenChange}
            title={title}
            graph={graph}
            loading={loading}
            timezone={locale.timezone}
            headerButtons={headerButtons}
            onRunButton={onRunButton}
            onRunTransition={onRunTransition}
            actionSlug={actionSlug}
            recordRef={recordUuid}
        />
    );
}
